import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { 
  Eye, 
  Share2, 
  MoreHorizontal, 
  Edit3, 
  Copy, 
  Trash2, 
  Clock,
  FileText,
  Users
} from 'lucide-react';
import { Generation } from '@/hooks/useGenerations';
import { formatDistanceToNow } from 'date-fns';

interface GenerationCardProps {
  generation: Generation;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onShare: (id: string) => void;
}

const GenerationCard: React.FC<GenerationCardProps> = ({
  generation,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
  onShare
}) => {
  const getTypeIcon = () => {
    switch (generation.generation_type) {
      case 'survey':
        return <FileText className="w-4 h-4" />;
      case 'conversation':
        return <Users className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getStatusColor = () => {
    switch (generation.status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const questionCount = generation.generated_content?.questions?.length || 
                       generation.generated_content?.length || 0;

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {getTypeIcon()}
            <CardTitle className="text-lg line-clamp-1">
              {generation.title || 'Untitled Generation'}
            </CardTitle>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onView}>
                <Eye className="w-4 h-4 mr-2" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEdit}>
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(generation.id)}>
                <Copy className="w-4 h-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onShare(generation.id)}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onDelete(generation.id)}
                className="text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Badge variant="outline" className="capitalize">
            {generation.generation_type}
          </Badge>
          <Badge className={getStatusColor()}>
            {generation.status}
          </Badge>
          {generation.is_public && (
            <Badge variant="secondary">
              Public
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              {questionCount} questions
            </div>
            {generation.estimated_duration_minutes && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {generation.estimated_duration_minutes} min
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {generation.view_count} views
            </div>
            <div className="flex items-center gap-1">
              <Share2 className="w-4 h-4" />
              {generation.share_count} shares
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            Updated {formatDistanceToNow(new Date(generation.updated_at), { addSuffix: true })}
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onView}
              className="flex-1"
            >
              View
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={onEdit}
              className="flex-1"
            >
              Edit
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GenerationCard;